import healthKnowledgeBase from "../data/healthKnowledgeBase";

// Simple tokenizer
function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

// Stopwords to ignore
const STOPWORDS = new Set([
  "the", "and", "for", "are", "but", "not", "you", "all", "can", "had",
  "her", "was", "one", "our", "out", "has", "have", "been", "some", "them",
  "than", "its", "over", "such", "that", "this", "with", "will", "each",
  "from", "they", "what", "about", "would", "there", "their", "which",
  "could", "other", "into", "more", "when", "very", "your", "how", "who",
  "need", "should", "does", "doing", "did", "get", "got",
]);

// Compute IDF across all chunks
const allTokens = healthKnowledgeBase.map((chunk) =>
  new Set([
    ...tokenize(chunk.content),
    ...chunk.keywords,
    ...tokenize(chunk.topic),
  ])
);

function idf(term) {
  const docsWithTerm = allTokens.filter((s) => s.has(term)).length;
  if (docsWithTerm === 0) return 0;
  return Math.log(allTokens.length / docsWithTerm) + 1;
}

// Intent detection — what kind of question is this?
function detectIntent(query) {
  const lower = query.toLowerCase();
  if (/what.*screening|which.*screening|do i need.*screen|when.*screen|how often.*screen/.test(lower)) {
    return "screening_overview";
  }
  if (/risk|chance|likely|prone|susceptible|am i at risk/.test(lower)) {
    return "risk_assessment";
  }
  if (/prevent|reduce|lower.*risk|protect|avoid/.test(lower)) {
    return "prevention";
  }
  if (/symptom|sign|warning|notice|feel/.test(lower)) {
    return "symptoms";
  }
  if (/what is|explain|tell me about|how does/.test(lower)) {
    return "education";
  }
  return "general";
}

// Boost scores based on intent
function intentBoost(chunk, intent) {
  const topic = chunk.topic.toLowerCase();
  const id = chunk.id.toLowerCase();
  
  switch (intent) {
    case "screening_overview":
      // Prefer screening chunks over risk factor chunks
      if (topic.includes("screening") || id.includes("screening")) return 2.0;
      if (topic.includes("risk")) return 0.5;
      return 1.0;
    case "risk_assessment":
      if (topic.includes("risk") || id.includes("risk")) return 2.0;
      return 1.0;
    case "prevention":
      if (id.includes("prevention") || topic.includes("prevention")) return 3.0;
      if (topic.includes("management")) return 1.5;
      return 1.0;
    default:
      return 1.0;
  }
}

/**
 * Search the knowledge base for the most relevant chunks.
 * @param {string} query - User's question
 * @param {number} topK - Number of results to return
 * @returns {Array<{chunk: object, score: number}>}
 */
export function searchKnowledgeBase(query, topK = 3) {
  const queryTokens = tokenize(query).filter((t) => !STOPWORDS.has(t));
  if (queryTokens.length === 0) return [];

  const intent = detectIntent(query);

  const scored = healthKnowledgeBase.map((chunk) => {
    const contentTokens = tokenize(chunk.content);
    const keywordSet = new Set(chunk.keywords.map((k) => k.toLowerCase()));
    const topicTokens = new Set(tokenize(chunk.topic));

    let score = 0;

    for (const qt of queryTokens) {
      const termIdf = idf(qt);

      // Keyword match (highest weight)
      if (keywordSet.has(qt)) {
        score += 3.0 * termIdf;
      }

      // Topic match
      if (topicTokens.has(qt)) {
        score += 2.0 * termIdf;
      }

      // Content term frequency
      const tf = contentTokens.filter((t) => t === qt).length;
      if (tf > 0) {
        score += (1 + Math.log(tf)) * termIdf;
      }

      // Partial keyword match (substring)
      for (const kw of chunk.keywords) {
        if (kw.includes(qt) && kw !== qt) {
          score += 0.5 * termIdf;
          break;
        }
      }
    }

    // Apply intent-based boosting
    score *= intentBoost(chunk, intent);

    return { chunk, score };
  });

  // For general screening questions, boost the most common/important screenings
  const MAJOR_SCREENINGS = ["breast_cancer_screening", "colorectal_cancer_screening", "lung_cancer_screening", "cervical_cancer_screening", "prostate_cancer_screening"];
  if (intent === "screening_overview") {
    for (const item of scored) {
      if (MAJOR_SCREENINGS.includes(item.chunk.topic)) {
        item.score *= 1.5;
      }
    }
  }

  // Deduplicate: if multiple chunks from same topic, prefer highest scoring
  const topicBest = new Map();
  for (const item of scored) {
    if (item.score <= 0) continue;
    const topic = item.chunk.topic;
    if (!topicBest.has(topic) || topicBest.get(topic).score < item.score) {
      topicBest.set(topic, item);
    }
  }

  return Array.from(topicBest.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, topK);
}
